import {
  UNAVAILABLE_CODES,
  UNAVAILABLE_MESSAGE_CODES,
} from "@/constants/no-results";
import i18n from "@/locales";

const UNSET_CACHE_VALUE = undefined;
const UNKNOWN_MESSAGE_KEY = "unknown";


export default class UnavailableResponse {
  constructor(code, data, zipCode) {
    this._initializePrivateCacheProperties();

    this.code = code;
    this.data = data;
    this.zipCode = zipCode;

  }

  /**
   * Default messaging to display for this response
   *
   * @returns String
   */
  message() {
    if (this._messageCache !== UNSET_CACHE_VALUE) {
      return this._messageCache;
    }

    return this._messageCache = i18n.t(`unavailableResponse.${this.messageKey()}`, this.messageData());
  }

  /**
   * Key for default message to return for this response
   *
   * @returns String
   */
  messageKey() {
    if (this._messageKeyCache !== UNSET_CACHE_VALUE) {
      return this._messageKeyCache;
    }

    return this._messageKeyCache = UNAVAILABLE_MESSAGE_CODES[this.code] ?? UNKNOWN_MESSAGE_KEY;
  }

  /**
   * Data object containing any data required for message
   *
   * @returns Object
   */
  messageData() {
    if (this._messageDataCache !== UNSET_CACHE_VALUE) {
      return this._messageDataCache;
    }

    let d = {};

    if (this.needsStateData()) {
      d.state = this.data.state;
    }

    if (this.needsZipCodeData()) {
      d.zipCode = this.zipCode;
    }

    return this._messageDataCache = d;
  }

  /**
   * Is this a response that needs state data for its message?
   *
   * @returns Boolean
   */
  needsStateData() {
    if (this._needsStateDataCache !== UNSET_CACHE_VALUE) {
      return this._needsStateDataCache;
    }

    return this._needsStateDataCache = this.code === UNAVAILABLE_CODES.STATE_ALCOHOL_RESTRICTION;
  }

  /**
   * Is this a response that needs zip code data for its message?
   *
   * @returns Boolean
   */
  needsZipCodeData() {
    if (this._needsZipCodeDataCache !== UNSET_CACHE_VALUE) {
      return this._needsZipCodeDataCache;
    }

    return this._needsZipCodeDataCache = [
      UNAVAILABLE_CODES.NO_OPTIONS,
      UNAVAILABLE_CODES.ZIP_CODE_UNAVAILABLE,
      UNAVAILABLE_CODES.FLORIST_UNAVAILABLE,
      UNAVAILABLE_CODES.GIFT_BASKET_UNAVAILABLE,
      UNAVAILABLE_CODES.TREE_UNAVAILABLE,
      UNAVAILABLE_CODES.DROPSHIP_UNAVAILABLE,
      UNAVAILABLE_CODES.BALLOON_UNAVAILABLE,
    ].includes(this.code)
      || this.messageKey() === UNKNOWN_MESSAGE_KEY;
  }

  /**
   * Called only during initialization to explicitly set all
   * "private" properties used for caching method values
   * Technically, we could just not initialize these and they would
   * implicitly be set to undefined, but this feels like a slightly
   * safer guard against quirky browser/type behavior
   *
   * @returns void
   */
  _initializePrivateCacheProperties() {
    this._messageCache
      = this._messageKeyCache
      = this._messageDataCache
      = this._needsStateDataCache
      = this._needsZipCodeDataCache
      = UNSET_CACHE_VALUE;
  }
}